import type { JourneyId } from '@epilot/journey-logic-commons/src/types/next'
import type { AnalyticsPlugin } from 'analytics'
import { v4 as uuidv4 } from 'uuid'

import type { Session } from '../../services/analytics'
import {
  createSession as _createSession,
  track as _track,
  endSession as _endSession
} from '../../services/analytics'
import { debug } from '../../utils/debug'

import { ConfigSchema } from './schemas'
import type { BrowserDetails, EpilotPluginMethods } from './types'

/**
 * TODO: consider adding retries to underlying service calls
 * TODO: parse payload types against schema
 */
export const epilotPlugin = (): AnalyticsPlugin => {
  let readyState = false
  let session: Session | undefined
  let publicToken: string | null | undefined
  let sessionJourneyId: JourneyId | undefined

  const createSession: EpilotPluginMethods['createSession'] = async (
    token,
    journey,
    launcherJourney,
    event
  ) => {
    try {
      // won't start or end session if journey is the same
      if (session && sessionJourneyId === journey.id) return

      // otherwise end the previous session
      if (session) await endSession()

      sessionJourneyId = journey.id
      publicToken = token

      session = {
        id: uuidv4(),
        startTime: new Date().toISOString(),
        details: {
          ...getBrowserDetails(),
          triggerEventType: event?.type,
          journeyName: journey.name,
          launcherJourneyId: launcherJourney?.id
        }
      }

      await _createSession(publicToken, {
        category: 'journey',
        categoryDetails: {
          journeyId: journey.id,
          journeyName: journey.name
        },
        session
      })

      readyState = true
    } catch (originalError) {
      readyState = false
      session = undefined
      publicToken = undefined
      sessionJourneyId = undefined

      const error = new Error('Failed to create analytics session')

      error.cause = originalError

      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  const endSession = async () => {
    try {
      if (!session) return

      await _endSession(publicToken, session.id, {
        category: 'journey',
        categoryDetails: {
          journeyId: sessionJourneyId
        },
        session: {
          ...session,
          endTime: new Date().toISOString()
        }
      })
    } catch (originalError) {
      const error = new Error('Failed to end session')

      error.cause = originalError

      // eslint-disable-next-line no-console
      console.error(error)
    } finally {
      readyState = false
      session = undefined
      publicToken = undefined
      sessionJourneyId = undefined
    }
  }

  // return object for analytics to use
  return {
    name: 'epilot',
    config: {
      // add defaults here
    },
    initialize: ({ config }) => {
      const parse = ConfigSchema.safeParse(config)

      if (!parse.success) {
        const error = new Error('Invalid config for Journey analytics plugin')

        error.cause = parse.error

        throw error
      }
    },

    page: ({ payload }) => {
      const { journeyId, journeyName, ...details } = payload.properties

      // ensure session exists regardless of inner state
      if (!session) return

      // skip events without 'journeyId' given the backend cannot process
      if (!journeyId) {
        return void debug(
          "[analytics.epilotPlugin] Skipping page event without 'journeyId'",
          { payload }
        )
      }

      _track(publicToken, session.id, {
        category: 'journey',
        categoryDetails: {
          journeyId,
          journeyName
        },
        events: [
          {
            type: 'step_navigation',
            details,
            timestamp: new Date(payload.meta.ts).toISOString()
          }
        ]
      })
    },

    track: ({ payload }) => {
      const { journeyId, journeyName, ...details } = payload.properties

      // ensure session exists regardless of inner state
      if (!session) return

      // skip events without 'journeyId' given the backend cannot process
      if (!journeyId) {
        return void debug(
          "[analytics.epilotPlugin] Skipping track event without 'journeyId'",
          { payload }
        )
      }

      _track(publicToken, session.id, {
        category: 'journey',
        categoryDetails: {
          journeyId,
          journeyName
        },
        events: [
          {
            type: payload.event,
            details,
            timestamp: new Date(payload.meta.ts).toISOString()
          }
        ]
      })
    },

    /**
     * Indicates whether the plugin is ready to receive data.
     * @returns boolean
     */
    loaded: (): boolean => readyState,

    /**
     * Clear all information about the visitor and reset analytic state
     */
    reset: () => void endSession(),

    methods: {
      /**
       * Creates a new session for the epilot plugin.
       *
       * @example analytics.plugins['epilot'].createSession()
       */
      createSession
    }
  }
}

export const getBrowserDetails = (): BrowserDetails => {
  const userAgent = navigator.userAgent
  const deviceType = /Mobi|Android/i.test(userAgent) ? 'Mobile' : 'Desktop'

  const osMap = [
    { name: 'Windows', pattern: /Win/i },
    { name: 'MacOS', pattern: /Mac/i },
    { name: 'Linux', pattern: /Linux/i },
    { name: 'Android', pattern: /Android/i },
    { name: 'iOS', pattern: /like Mac/i }
  ]

  const osType =
    osMap.find((os) => os.pattern.test(userAgent))?.name || 'Unknown OS'
  const browserTypeAndVersion = userAgent
  const screenResolution = `${screen.width}x${screen.height}`
  const viewportSize = `${window.innerWidth}x${window.innerHeight}`
  const colorDepth = screen.colorDepth
  const languagePreference = navigator.language

  return {
    deviceType,
    osType,
    browserTypeAndVersion,
    screenResolution,
    viewportSize,
    colorDepth,
    languagePreference
  }
}
